import { z } from 'zod';

// Base schema for shared fields (used by both create and edit)
const baseUserSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(50, 'Username must be at most 50 characters')
    .regex(
      /^[a-zA-Z0-9_]+$/,
      'Username can only contain letters, numbers, and underscores'
    ),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Invalid email address')
    .max(255, 'Email is too long'),
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(100, 'First name is too long'),
  middleName: z.string().max(100, 'Middle name is too long').optional(),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(100, 'Last name is too long'),
  employeeIdNumber: z
    .string()
    .min(1, 'Employee ID is required')
    .max(50, 'Employee ID is too long')
    .optional(),
  mobileNumber: z.string().max(20, 'Mobile number is too long').optional(),
  startDate: z.string().nullable().optional(),
  birthdate: z.string().nullable().optional(),
  departmentId: z.string().max(50).nullable().optional(),
  departmentName: z.string().max(200).nullable().optional(),
  positionId: z.string().max(50).nullable().optional(),
  positionName: z.string().max(200).nullable().optional(),
  isActive: z.boolean(),
  netSuiteEmployeeId: z.string().nullable().optional(),
});

// Create user schema - no password fields (temporary password assigned by backend)
export const createUserSchema = baseUserSchema;

// Edit user details schema - no username (read-only after creation), no password fields
// Employment info fields (employeeIdNumber, department, position) are read-only in the edit form
// and sourced from HR/employee records — excluded from the editable form schema
export const editUserDetailsSchema = baseUserSchema.omit({
  username: true,
  employeeIdNumber: true,
  departmentId: true,
  departmentName: true,
  positionId: true,
  positionName: true,
  netSuiteEmployeeId: true,
});

// Password validation rules (used by change password form)
// Must match backend MustBeStrongPassword() rules in PasswordValidationExtensions.cs
const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password is too long')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one special character');

// Change password schema - for admin to set new password (no current password needed)
export const changePasswordSchema = z
  .object({
    newPassword: passwordSchema,
    confirmNewPassword: z.string().min(1, 'Please confirm new password'),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
  });

// Self-service change password schema - requires current password
export const selfChangePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: passwordSchema,
    confirmNewPassword: z.string().min(1, 'Please confirm new password'),
  })
  .refine((data) => data.newPassword !== data.currentPassword, {
    message: 'New password must be different from your current password',
    path: ['newPassword'],
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: 'Passwords do not match',
    path: ['confirmNewPassword'],
  });

export type CreateUserFormData = z.infer<typeof createUserSchema>;
export type EditUserDetailsFormData = z.infer<typeof editUserDetailsSchema>;
export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;
export type SelfChangePasswordFormData = z.infer<typeof selfChangePasswordSchema>;
